import React, { useContext, useMemo } from 'react';

//mui imports==========
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import useStyles from './styles';
import { Box } from '@mui/system';
import { Chip } from '@mui/material';
//=====================

import { ACCESS_CONTROL_CONSTANTS } from '../../core/constants';
import { UserContext } from '../../Contexts/UserContext';
import InfoButton from '../InfoButton';
const { SYSTEMS, UI_ACCESS, TYPES } = ACCESS_CONTROL_CONSTANTS;

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const menuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250
		}
	}
};

const DropDownSingle = ({
	keyData,
	list,
	label,
	object,
	updateObject,
	formControlVariant,
	disabled,
	containerStyles,
	formControlStyles,
	formControlSize,
	labelStyles,
	isObjectEditor = true,
	directValue,
	updateDirectValue,
	isMandatory = false,
	selectInputStyles,
	forceDeselect = false,
	showInfoButton = false,
	infoMessage = '',
	infoButtonProps = {},
	system = SYSTEMS.FRAMERATE
}) => {
	const { getAccessControlSet } = useContext(UserContext);

	/**
	 * This variable contains a set of strings that signifies if this input is either visible/disabled/hidden
	 * @type {Set<string>}
	 */
	const dropDownSingleControlledAccessSet = useMemo(() => {
		return getAccessControlSet(system, TYPES.INPUT_NAMES, keyData);
	}, [getAccessControlSet, keyData, system]);

	const getValue = () => {
		if (isObjectEditor) {
			return object[keyData] ? object[keyData] : '';
		}
		return directValue || '';
	};
	const classes = useStyles();
	const displayLabel = isMandatory ? `${label} *` : label;

	const getDisableStyle = () => {
		return { display: 'none' };
	};

	/**
	 * @name handleDeSelect
	 * @description Mui Single Dropdown doesnt support item deselect by default. This function is a workaround for that.
	 * When a same selected item is clicked again from dropdown it will deselect that item and make the property an empty string.
	 * @param {string|number} valueSelectedNow
	 * @param {boolean} itemDisabledFromSelection
	 */
	const handleDeSelect = (valueSelectedNow, itemDisabledFromSelection) => {
		if (itemDisabledFromSelection) {
			return;
		}
		if (forceDeselect && valueSelectedNow === getValue()) {
			if (isObjectEditor) {
				updateObject((obj) => {
					let deepClonedObj = structuredClone(obj);
					return { ...deepClonedObj, [keyData]: '' };
				});
			} else {
				updateDirectValue('');
			}
		}
	};

	return (
		<>
			{!dropDownSingleControlledAccessSet.has(UI_ACCESS.HIDE) && (
				<Box className={classes.dropDownContainer} style={containerStyles || {}}>
					<FormControl
						className={classes.formControl}
						variant={formControlVariant || 'outlined'}
						style={formControlStyles || {}}
						size={formControlSize || ''}
					>
						<Typography
							className={classes.heading}
							fontWeight={600}
							variant="h6"
							style={labelStyles || {}}
						>
							{displayLabel}
							{showInfoButton && label?.trim() && (
								<InfoButton message={infoMessage} {...infoButtonProps} />
							)}
						</Typography>
						<Select
							labelId={`${keyData}-input-width-label`}
							id={`${keyData}-input-width-label`}
							value={getValue()}
							onChange={(e) => {
								if (isObjectEditor) {
									updateObject((obj) => {
										const deepClonedObj = structuredClone(obj);
										return { ...deepClonedObj, [keyData]: e.target.value };
									});
								} else {
									updateDirectValue(e.target.value);
								}
							}}
							renderValue={(selected) => {
								const selectedData = list.find(
									(data) => (typeof data === 'string' ? data : data.value) === selected
								);
								const selectedLabel =
									typeof selectedData === 'string' ? selectedData : selectedData?.label;
								const isNew = typeof selectedData === 'object' && selectedData?.isNew;
								return (
									<Box
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'center'
										}}
									>
										{selectedLabel}
										{isNew && (
											<Chip
												label={'NEW'}
												color="primary"
												style={{ marginLeft: 'auto', height: '2.5rem' }}
											/>
										)}
									</Box>
								);
							}}
							style={selectInputStyles || {}}
							disabled={disabled || dropDownSingleControlledAccessSet.has(UI_ACCESS.DISABLE)}
							MenuProps={menuProps} // For menu items to be scrollable.
						>
							{list.map((data) => {
								const value = typeof data === 'string' ? data : data.value;
								const label = typeof data === 'string' ? data : data.label;
								// If data is object, we'll take disable property from it, else disableItem will be false
								const disableItem = typeof data === 'object' ? !!data.disable : false;
								return (
									<MenuItem
										value={value}
										key={value}
										style={
											disableItem
												? getDisableStyle()
												: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }
										}
										onClick={() => handleDeSelect(value, disableItem)}
									>
										{label}
										{data.isNew && (
											<Chip
												label={'NEW'}
												color="primary"
												style={{ marginLeft: 'auto', height: '2.5rem' }}
											/>
										)}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</Box>
			)}
		</>
	);
};

export default DropDownSingle;
